import { LocateFixed, Search } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { currentPosition, searchPlaces } from '../../services/weather';
import { useSettings, type Location } from '../../store/settings';
import { buttonClass, ghostButtonClass, inputClass, Section, Toggle } from './controls';

const ZONES: string[] = (() => {
  try {
    return (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.('timeZone') ?? [];
  } catch {
    return [];
  }
})();

export function WidgetsTab() {
  const clock = useSettings((s) => s.clock);
  const weather = useSettings((s) => s.weather);
  const lists = useSettings((s) => s.lists);
  const update = useSettings((s) => s.update);

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Location[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const search = async (e: FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const r = await searchPlaces(query.trim());
      setResults(r);
      if (!r.length) setError('No places found.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setBusy(false);
    }
  };

  const locate = async () => {
    setBusy(true);
    setError(null);
    try {
      update('weather', { location: await currentPosition() });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not get location');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Section title="Clock">
        <Toggle label="12-hour clock" checked={clock.hour12} onChange={(hour12) => update('clock', { hour12 })} />
        <Toggle label="Show seconds" checked={clock.showSeconds} onChange={(showSeconds) => update('clock', { showSeconds })} />
        <Toggle label="Show date" checked={clock.showDate} onChange={(showDate) => update('clock', { showDate })} />
        <label className="block text-sm">
          <span className="mb-1 block">Time zone</span>
          <select
            className={`${inputClass} bg-[var(--surface-strong)]`}
            value={clock.timeZone}
            onChange={(e) => update('clock', { timeZone: e.target.value })}
          >
            <option value="">This device ({Intl.DateTimeFormat().resolvedOptions().timeZone})</option>
            {ZONES.map((z) => (
              <option key={z} value={z}>
                {z.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
        </label>
      </Section>

      <Section title="Weather" description="7-day forecast from Open-Meteo.">
        <Toggle label="Show weather" checked={weather.enabled} onChange={(enabled) => update('weather', { enabled })} />
        <Toggle
          label="Fahrenheit"
          checked={weather.unit === 'fahrenheit'}
          onChange={(f) => update('weather', { unit: f ? 'fahrenheit' : 'celsius' })}
        />
        <p className="text-sm">
          Location: <strong>{weather.location?.name ?? 'not set'}</strong>
        </p>
        <form onSubmit={search} className="flex gap-2">
          <input
            className={inputClass}
            placeholder="Search a town or city"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search location"
          />
          <button className={buttonClass} disabled={busy} type="submit" aria-label="Search">
            <Search className="h-4 w-4" aria-hidden />
          </button>
          <button className={ghostButtonClass} disabled={busy} type="button" onClick={locate} aria-label="Use my location">
            <LocateFixed className="h-4 w-4" aria-hidden />
          </button>
        </form>
        {error && <p className="text-sm text-red-400">{error}</p>}
        {results.length > 0 && (
          <ul className="space-y-1">
            {results.map((r) => (
              <li key={`${r.latitude},${r.longitude}`}>
                <button
                  className="w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-[var(--border)]"
                  onClick={() => {
                    update('weather', { location: r });
                    setResults([]);
                    setQuery('');
                  }}
                >
                  {r.name}
                </button>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="To-do & shopping lists">
        <Toggle label="Show lists" checked={lists.enabled} onChange={(enabled) => update('lists', { enabled })} />
        <p className="muted text-xs">Lists sync between screens when you’re signed in.</p>
      </Section>
    </>
  );
}
